import MiniSearch from 'minisearch';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { NodeApi, Tree } from 'react-arborist';
import { contextMenu } from "react-contexify";
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Card, CardContent, Grid, InputAdornment, MenuItem, Select, Stack, TextField, Typography, useMediaQuery } from '@mui/material';
import ConfirmationModal from "../../components/common/ConfirmationModal";
import HamburgerMenuButton from "../../components/common/HamburgerMenuButton";
import { helpContext } from '../../context/HelpContext';
import { createArticleUrl, createTopicUrl } from '../../helpers/helpCenterUtils.js';
import { useHelpCenterRights } from "../../hooks/useHelpCenterRights";
import ContextMenu from './ContextMenu';
import TopicNodeToggleIcon from './TopicNodeToggleIcon';

const CONTEXT_MENU_ID = "topics_explorer_context_menu";

const initialConfirmationState = {
  confirm_token: undefined,
  data: {},
  message: undefined,
  showModal: false,
};

export default function TopicsExplorer() {
  const history = useHistory();
  const intl = useIntl();
  const treeRef = useRef(null);

  const isMobile = useMediaQuery('(max-width:600px)');

  const { canEdit } = useHelpCenterRights();

  const [confirmationState, setConfirmationState] = useState(initialConfirmationState);

  const [clickedNode, setClickedNode] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [sortOrder, setSortOrder] = useState('relevance');

  const { allArticles, deleteArticle, deleteTopic, loading, moveArticle, moveTopic, treeData } = useContext(helpContext);

  const collectArticleIds = (tree, folderId) => {
    let targetNode = null;

    const findNode = (nodes) => {
      for (const node of nodes) {
        if (node.id === folderId) {
          return node;
        }
        if (node.children) {
          const result = findNode(node.children);
          if (result) return result;
        }
      }
      return null;
    };

    const collectLeaves = (node, result = []) => {
      if (!node.children || node.children.length === 0) {
        result.push(node.id);
      } else {
        for (const child of node.children) {
          collectLeaves(child, result);
        }
      }
      return result;
    };

    targetNode = findNode(tree);
    if (!targetNode) return [];

    return collectLeaves(targetNode);
  };

  const articleSearch = useMemo(() => {
    const ms = new MiniSearch({
      fields: ['title', 'description', 'tags'],
      storeFields: ['articleId', 'title', 'description', 'tags'],
      searchOptions: { fuzzy: 0.3 }
    });
    ms.addAll(allArticles.map(article => ({
      id: article.id,
      articleId: article.articleId,
      title: article.title,
      description: article.description,
      tags: article.tags.join(' '),
    })));
    return ms;
  }, [allArticles, loading]);

  const filteredArticles = useMemo(() => {
    let results = [];

    if (search) {
      // Search mode: search all articles, ignore selectedCategory
      results = articleSearch.search(search, { prefix: true });
    } else if (selectedCategory) {
      // Browsing a category: show all articles in this category
      const articleIds = collectArticleIds(treeData, selectedCategory);
      results = allArticles
        .filter(article => articleIds.includes(article.id))
        .map(a => ({ ...a, match: {} }));
    } else {
      // No search or category: show all articles
      results = allArticles.map(a => ({ ...a, match: {} }));
    }

    if (sortOrder === 'title') {
      results.sort((a, b) => a.title.localeCompare(b.title));
    }

    return results;
  }, [search, sortOrder, selectedCategory, allArticles, articleSearch, treeData]);

  const checkIfArticle = (node) => node.data?.type === 'article';
  const checkIfTopic = (node) => node.data?.type === 'topic';




  const timeoutRef = useRef(null);
  const timeoutNodeId = useRef();

  const removeTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutNodeId.current = undefined;
    }
  }, []);

  const startTimeout = useCallback((node) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutNodeId.current = node.id;

    timeoutRef.current = setTimeout(() => {
      timeoutNodeId.current = undefined;
      node.open();
    }, 500);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutNodeId.current = undefined;
      }
    };
  }, []);




  const renderNode = ({ node, style, dragHandle }) => {

    const delayedExpansionActivated = (timeoutNodeId.current === node.id);

    if (node.id == mustCloseNodeId.current) {
      node.close();
      mustCloseNodeId.current = null;
    }

    let isArticle, isEmpty, isTopic;

    switch (node.data.type) {
      case 'article':
        isArticle = true;
        isEmpty = (allArticles.find(a => a.articleId === node.data.articleId)?.content || "").trim().length === 0;
        break;

      case 'topic':
        isTopic = true;
        isEmpty = node.children?.length === 0;
        break;

      default:
        break;
    }

    const handleToggle = (e) => {
      e.stopPropagation(); // Prevent row onClick from firing
      node.toggle();
    };

    return (
      <Box
        ref={dragHandle}
        style={style}
        onContextMenu={(e) => handleContextMenu(e, node.data)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          pl: `${node.level * 20}px`,
          height: 32,
          cursor: 'pointer',
          userSelect: 'none',
          backgroundColor: node.isSelected ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          }
        }}
      >
        {isTopic && (
          <TopicNodeToggleIcon
            node={node}
            onToggle={
              isEmpty ? undefined : handleToggle
            }
            sx={{
              display: 'flex',
              alignItems: 'center',
              mr: 0.5,
              opacity: (isEmpty ? 0.4 : 1),
              color: (isEmpty && canEdit) ? 'rgba(255, 0, 0, 0.7)' : 'inherit',
              transform: delayedExpansionActivated ? 'rotate(90deg)' : undefined,
              transition: delayedExpansionActivated ? 'transform 0.5s ease-in-out' : undefined,
            }}
          />
        )}

        {isArticle && (
          <DescriptionOutlinedIcon
            fontSize="small"
            sx={{
              mr: 0.5,
              opacity: 0.8,
            }}
          />
        )}

        <Typography variant="body2" noWrap
          sx={{
            color: (isEmpty && canEdit) ? 'rgba(255, 0, 0, 0.7)' : 'inherit',
          }}
        >
          {renderTitle(node, false)}
        </Typography>
      </Box>
    );
  };

  const renderTitle = (node, debug = false) => (
    <>
      {node.data.title}
      {debug && (
        <Typography component="span" display="contents" sx={{ fontSize: "x-small", fontWeight: 800 }}>
          &ensp;{node.data.id}
        </Typography>
      )}
    </>
  );

  const treeMenuOptions = [
    {
      enabled: true,
      hidden: false,
      label: <FormattedMessage id="helpCenter.contextMenu.createTopic" />,
      onClick: () => handleTopicAction({ action: "create", parentId: null }),
    }
  ];

  const TopicsExplorerHeader = ({ title, treeMenuOptions }) => (
    <Box sx={{ display: "flex" }}>
      <Typography variant="h6" sx={{ px: 2, pt: 1 }}>{title}</Typography>
      <Box sx={{ display: "flex", flex: "auto", alignItems: "flex-end", justifyContent: "flex-end" }}>
        <HamburgerMenuButton options={treeMenuOptions} />
      </Box>
    </Box>
  );

  const handleArticleAction = ({ action, articleId, title, topicId }) => {
    switch (action) {
      case "create":
        console.info(`** Creating a new article having topic ${topicId} as parent`);
        history.push({
          pathname: createArticleUrl(0),
          state: { edit: true, parentTopicId: topicId },
        });
        break;

      case "delete":
        console.info(`** Requesting confirmation of article deletion ${articleId} - ${title}`);
        setConfirmationState({
          showModal: true,
          message: <FormattedMessage id="helpCenter.article.confirmDelete.message" values={{ title }} />,
          confirm_token: "delete_article",
          data: { articleId }
        });
        break;

      case "edit":
        console.info(`** Editing article ${articleId} - ${title}`);
        history.push({
          pathname: createArticleUrl(articleId),
          state: { edit: true },
        });
        break;

      case "rename":
      default:
        console.warn("** Unhandled article action:", action);
        break;
    }
  };

  const handleConfirmation = () => {
    const { confirm_token, data } = confirmationState;
    switch (confirm_token) {
      case "delete_article":
        console.info("** Deleting article", { articleId: data.articleId });
        var parentNode = treeRef.current.get(`article_${data.articleId}`)?.parent;
        if (parentNode && parentNode.children.length <= 1) {
          parentNode.close();
        }
        deleteArticle(data.articleId);
        hideConfirmationModal();
        break;

      case "delete_topic":
        console.info("** Deleting topic", { topicId: data.topicId });
        var parentNode = treeRef.current.get(`topic_${data.topicId}`)?.parent;
        if (parentNode && parentNode.children.length <= 1) {
          parentNode.close();
        }
        deleteTopic(data.topicId);
        hideConfirmationModal();
        break;

      default:
        console.warn("** Unhandled confirm_token:", confirm_token);
        break;
    }
  };

  const handleContextMenu = (e, nodeData) => {
    e.preventDefault();
    e.stopPropagation();
    setClickedNode(nodeData);
    contextMenu.hideAll();
    setTimeout(() => {
      contextMenu.show({
        id: CONTEXT_MENU_ID,
        event: e,
      });
    }, 0);
  };

  const handleContextMenuClick = ({ event, props }) => {
    const { action, articleId, topicId, title, type } = props;
    switch (type) {
      case "article":
        handleArticleAction({ action, articleId, title, topicId });
        break;

      case "topic":
        handleTopicAction({ action, title, topicId });
        break;

      default:
        console.warn("** Unhandled node type:", type);
        break;
    }
  };

  const handleTopicAction = ({ action, title, topicId }) => {
    switch (action) {
      case "create":
        console.info(`** Creating a new topic having topic ${topicId} as parent`);
        history.push({
          pathname: createTopicUrl(0),
          state: { edit: true, parentTopicId: topicId },
        });
        break;

      case "delete":
        console.info(`** Requesting confirmation of topic deletion ${topicId} - ${title}`);
        setConfirmationState({
          showModal: true,
          message: <FormattedMessage id="helpCenter.topic.confirmDelete.message" values={{ title }} />,
          confirm_token: "delete_topic",
          data: { topicId }
        });
        break;

      case "edit":
        console.info(`** Editing topic ${topicId} - ${title}`);
        history.push({
          pathname: createTopicUrl(topicId),
          state: { edit: true },
        });
        break;

      case "rename":
      default:
        console.warn("** Unhandled topic action:", action);
        break;
    }
  };

  const hideConfirmationModal = () => setConfirmationState(initialConfirmationState);

  const highlightText = (text, terms = []) => {
    if (!terms.length || !text) return text;
    const filteredTerms = terms.map(n => n.trim()).filter(n => n);
    const pattern = new RegExp(`(${filteredTerms.join('|')})`, 'gi');
    const parts = text.split(pattern);
    return parts.map((part, index) =>
      filteredTerms.some(term => new RegExp(term, 'i').test(part))
        ? <mark key={index} style={{ backgroundColor: '#cce5ff' }}>{part}</mark>
        : part
    );
  };


  /**
   * Node id of a node that must be closed.
   */
  const mustCloseNodeId = useRef(null);

  /**
   * Marks an empty node as "must be closed".
   * 
   * @param {NodeApi} node
   */
  const closeIfNoChildren = (node) => {
    if (node && node.children && node.children.length < 1) {
      mustCloseNodeId.current = node.id;
    }
  };


  /**
   * Calculates the actual index among the child articles of a topic node.
   * 
   * @param {NodeApi} dropNode Topic node
   * @param {number} dropIndex Drop index
   * 
   * @returns {number} Index among the child articles of the topic node.
   */
  const calculateArticleIndex = (dropNode, dropIndex) => {
    const firstArticleIndex = dropNode.children.findIndex(n => n.data.type === 'article');
    return (firstArticleIndex < 0) ? 0 : dropIndex - firstArticleIndex;
  };

  /**
   * Checks if the drop position is adjacent to the position of dragged node.
   * 
   * @param {NodeApi} dragNode
   * @param {NodeApi} dropNode
   * @param {number} dropIndex
   * 
   * @returns {boolean} True if adjacent.
   */
  const checkIfAdjacentDragAndDropPosition = (dragNode, dropNode, dropIndex) => {
    if (dropNode.id === dragNode.parent.id) {
      var distance = dropIndex - dragNode.childIndex;
      return (distance == 0 || distance == 1);
    }
    return false;
  };


  return (
    <Box sx={{ display: 'flex', gap: 2, flexDirection: isMobile ? 'column' : 'row' }}>
      <Box sx={{ width: isMobile ? '100%' : 330, maxHeight: '100vh', overflowY: 'auto', borderRight: isMobile ? 'none' : '1px solid #ccc' }}>
        {canEdit &&
          <TopicsExplorerHeader
            title={<FormattedMessage id="helpCenter.topics" />}
            treeMenuOptions={treeMenuOptions}
          />
        }
        <Tree
          ref={treeRef}
          data={treeData}
          openByDefault={!true}
          selection={selectedCategory}
          rowHeight={32}
          indent={20}

          disableDrag={!canEdit}

          disableDrop={(props) => {
            const { dragNodes, index, parentNode } = props;

            // Disable multi-node drop.
            //
            if (dragNodes.length !== 1) {
              return true;
            }

            // Disable drop when target is an article.
            //
            if (checkIfArticle(parentNode)) {
              return true;
            }

            // Delayed expansion of hovered node (topic).
            //
            if (parentNode.isClosed) {
              if (parentNode.children.length === 0) {
                return false;
              }
              if (timeoutNodeId.current !== parentNode.id) {
                startTimeout(parentNode);
              }
              return true;
            }
            else if (timeoutNodeId.current) {
              removeTimeout();
            }

            const dragNode = dragNodes[0];
            const dragType = dragNode.data.type;

            switch (dragType) {
              case 'article':
                const nextSibling = parentNode.children[index];
                return nextSibling && checkIfTopic(nextSibling);

              case 'topic':
                const prevSibling = parentNode.children[index - 1];
                return prevSibling && checkIfArticle(prevSibling);

              default:
                return true;
            }
          }}

          onActivate={(node) => {
            const type = node?.data?.type;
            switch (type) {
              case 'article':
                const articleUrl = createArticleUrl(node.data.articleId);
                history.push(articleUrl);
                break;

              case 'topic':
                setSelectedCategory(node.id);
                break;

              default:
                console.warn("** Unhandled node type:", type);
            }
          }}

          //onContextMenu: Not needed
          //onCreate: Not needed
          //onDelete: Not needed
          //onRename: Not needed

          onMove={(props) => {
            const dragNode = props.dragNodes[0];
            const dropNode = props.parentNode ?? treeRef.current.root;
            const dropIndex = props.index;

            const { type, topicId, articleId } = dragNode.data;

            // Do not move the node if it is dropped adjacent to itself.
            //
            var isAdjacent = checkIfAdjacentDragAndDropPosition(dragNode, dropNode, dropIndex);
            if (isAdjacent) {
              return;
            }

            switch (type) {
              case 'article':
                const adjustedIndex = calculateArticleIndex(dropNode, dropIndex);
                moveArticle({ articleId, parentTopicId: dropNode.data?.topicId, index: adjustedIndex });
                break;

              case 'topic':
                moveTopic({ topicId, parentTopicId: dropNode.data?.topicId, index: dropIndex });
                break;

              default:
                console.warn("** Unhandled node type:", type);
                break;
            }

            closeIfNoChildren(dropNode);
          }}
        >
          {renderNode}
        </Tree>
      </Box>

      <Box sx={{ flex: 1 }}>
        <TextField
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`${intl.formatMessage({ id: "common.search" })}...`}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {/* Show search results if searching */}
        {search ? (<>
          <Stack direction={isMobile ? 'column' : 'row'} justifyContent="space-between" alignItems={isMobile ? 'flex-start' : 'center'} spacing={1} sx={{ mb: 1 }}>
            <Typography variant="subtitle2">
              {filteredArticles.length} träffar
            </Typography>
            <Select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              size="small"
            >
              <MenuItem value="relevance">Relevans</MenuItem>
              <MenuItem value="title">Artikeltitel</MenuItem>
            </Select>
          </Stack>
          <Box sx={{ mb: 4 }}>
            {filteredArticles.map((article) => {
              const matchTerms = search.split(/\s+/);
              return (
                <Box
                  key={article.id}
                  onClick={() => history.push(createArticleUrl(article.articleId))}
                  sx={{
                    py: 1.5,
                    borderBottom: '1px solid #eee',
                    cursor: 'pointer',
                    px: isMobile ? 1 : 0,
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' }
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 0.5 }}>
                    {highlightText(article.title, matchTerms)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {highlightText(article.description, matchTerms)}
                  </Typography>
                </Box>
              );
            })}
          </Box></>
        ) : (
          // Otherwise, show category or all articles as cards
          <Grid container spacing={2}>
            {filteredArticles.map((article) => (
              <Grid item xs={12} sm={6} md={4} key={article.id}>
                <Card sx={{ height: '100%', cursor: 'pointer' }}
                  onClick={() => history.push(createArticleUrl(article.articleId))}
                >
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {article.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {article.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {canEdit &&
        <ContextMenu
          menuId={CONTEXT_MENU_ID}
          nodeData={clickedNode}
          onItemClick={handleContextMenuClick}
        />
      }

      {confirmationState?.showModal && (
        <ConfirmationModal
          //disableYesButton={deleteInProgress}
          handleClose={hideConfirmationModal}
          message={
            <Box sx={{ textWrapStyle: "balance", whiteSpace: "pre-wrap" }}>
              {confirmationState.message}
            </Box>
          }
          onClick={handleConfirmation}
          open={confirmationState.showModal}
        />
      )}
    </Box>
  );
}
